import { Moon, Sun } from "lucide-react";
import React, { createContext, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  currentTheme: "light" | "dark"; // The actual applied theme
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Get theme from localStorage or default to 'dark'
    const savedTheme = localStorage.getItem("theme") as Theme;
    return savedTheme === "light" || savedTheme === "dark"
      ? savedTheme
      : "dark";
  });

  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">("dark");

  useEffect(() => {
    const root = document.documentElement;

    // Remove previous theme classes
    root.removeAttribute("data-theme");
    root.classList.remove("light", "dark");

    const resolvedTheme = theme;
    setCurrentTheme(resolvedTheme);
    root.setAttribute("data-theme", resolvedTheme);
    root.classList.add(resolvedTheme);

    // Save to localStorage
    localStorage.setItem("theme", theme);
  }, [theme]);

  const contextValue: ThemeContextType = {
    theme,
    setTheme,
    currentTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// Theme toggle component
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    const themes: Theme[] = ["light", "dark"];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    if (nextTheme) {
      setTheme(nextTheme);
    }
  };

  const getThemeIcon = () => {
    switch (theme) {
      case "light":
        return <Moon className="w-4 h-4 text-secondary" />;
      case "dark":
        return <Sun className="w-4 h-4"></Sun>;
    }
  };

  return (
    <button
      onClick={toggleTheme}
      className="flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-background-secondary hover:bg-hover transition-colors duration-fast"
      title={`Current theme: ${theme}`}
      aria-label={`Switch theme. Current: ${theme}`}
    >
      {getThemeIcon()}
    </button>
  );
}
